export type Operator = '+' | '−' | '×' | '÷';

export interface Expression {
  a: number;
  b: number;
  op: Operator;
}

const QML_OP_MAP: Record<string, Operator> = {
  '+': '+',
  '-': '−',
  '*': '×',
  '/': '÷',
};

export function parseQmlOperator(qml: string): Operator {
  const trimmed = qml.trim();
  return QML_OP_MAP[trimmed] ?? '+';
}

export function genNumber(goal: number): number {
  if (Math.random() < 0.5) return goal;
  const sign = Math.random() < 0.5 ? -1 : 1;
  return goal + sign * (Math.floor(Math.random() * (goal - 1)) + 1);
}

function splitPlus(term: number): [number, number] {
  const odd = term % 2;
  let t1 = Math.floor(term / 2);
  let t2 = t1 + odd;
  const shift = Math.floor(Math.random() * t1);
  t1 += shift;
  t2 -= shift;
  return Math.random() < 0.5 ? [t1, t2] : [t2, t1];
}

function splitMinus(term: number): [number, number] {
  const shift = Math.floor(Math.random() * (term + 1));
  return [term + shift, shift];
}

function splitMult(term: number, goal: number): [number, number] {
  const factors: number[] = [];
  for (let d = 1; d <= term; d++) {
    if (goal % d === 0) factors.push(d);
  }
  if (factors.length === 0) return [1, term];
  const t1 = factors[Math.floor(Math.random() * factors.length)]!;
  const t2 = Math.floor(term / t1);
  return Math.random() < 0.5 ? [t1, t2] : [t2, t1];
}

function splitDiv(term: number): [number, number] {
  const k = Math.floor(Math.random() * 6) + 1;
  const t1 = term * k;
  const t2 = k;
  return [t1, t2];
}

export function generateCell(operator: Operator, goal: number): Expression {
  const target = genNumber(goal);
  let pair: [number, number];
  if (operator === '+') pair = splitPlus(Math.max(target, 0));
  else if (operator === '−') pair = splitMinus(Math.max(target, 0));
  else if (operator === '×') pair = splitMult(Math.max(target, 1), goal);
  else pair = splitDiv(Math.max(target, 1));
  return { a: pair[0], b: pair[1], op: operator };
}

export function evaluate(expr: Expression): number {
  if (expr.op === '+') return expr.a + expr.b;
  if (expr.op === '−') return expr.a - expr.b;
  if (expr.op === '×') return expr.a * expr.b;
  return expr.b === 0 ? Number.NaN : expr.a / expr.b;
}

export function generateGrid(operator: Operator, goal: number, size: number = 36): Expression[] {
  const cells: Expression[] = [];
  for (let i = 0; i < size; i++) cells.push(generateCell(operator, goal));
  // Garante pelo menos 4 expressões corretas no grid (raramente o random vira tudo errado)
  const correctCount = cells.filter((e) => evaluate(e) === goal).length;
  if (correctCount < 4) {
    const missing = 4 - correctCount;
    for (let i = 0; i < missing; i++) {
      const idx = Math.floor(Math.random() * size);
      const target = goal;
      let pair: [number, number];
      if (operator === '+') pair = splitPlus(target);
      else if (operator === '−') pair = splitMinus(target);
      else if (operator === '×') pair = splitMult(target, goal);
      else pair = splitDiv(target);
      cells[idx] = { a: pair[0], b: pair[1], op: operator };
    }
  }
  return cells;
}
